import { h, FunctionalComponent } from 'preact';
import { onKeyboardSelection } from 'helpers/operators';
import './footer.scss';
import { useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { ApplicationContext } from 'contexts/use-application-setup';
import { useTranslation, useUwBranding } from 'hooks';
import { Logo } from './logo';
import { ShortpointLogo } from './partners-logo/shortpoint-logo';
import { LeosLogo } from './partners-logo/leos-logo';
import { NagishLiLogo } from './partners-logo/nagishli-logo';
import { NazoozLogo } from './partners-logo/nazooz-logo';
import { AltmeyerLogo } from './partners-logo/altmeyer-logo';
import { PortcitymarketingLogo } from './partners-logo/portcitymarketing-logo';
import { GenericPartnerLogo } from './partners-logo/generic-partner-logo';
import { useFooter } from './use-footer';
import { DigitouchLogo } from './partners-logo/digitouch-logo';
import { AdaguardianLogo } from './partners-logo/adaguardian-logo';
import { JoyDigitalLogo } from './partners-logo/joydigital-logo';
import { MadeByCatLogo } from './partners-logo/madebycat-logo';
import { XperientsLogo } from './partners-logo/xperients-logo';
import { SorceLogo } from './partners-logo/sorce-logo';
import { OctecLogo } from './partners-logo/octec-logo';

const truncate = (text: string, limit: number) =>
  text.length > limit ? text.substr(0, limit - 1) : text;

const PAUSE_ANIMATIONS_FEATURE_KEY = 'userway-s13';
const FOOTER_ANIMATION_DELAY = 300;

export const Footer: FunctionalComponent = () => {
  const context = useContext(ApplicationContext);
  const { translate } = useTranslation();
  const {
    accessibilityStatementText,
    asteriskVisible,
    isPartnerDefaultLogoEnvironment,
    isGenericPartnerCustomLogoEnvironment,
    manageLinkVisible,
    reportProblemVisible,
    accessibilityStatementVisible,
    isFooterNavLinksVisible,
    disabledGoToBtn,
    createGoTo,
    // callbackRef,
    isMainMenuUrl,
    isLogged,
    backState,
    accessibilityStatementLink,
  } = useFooter({ context, translate });

  const { isWhiteLabeled } = useUwBranding();

  const isCustomLogoEnvironment =
    isPartnerDefaultLogoEnvironment || isGenericPartnerCustomLogoEnvironment;

  const isPauseAnimationFeatureActive = useMemo<boolean>(
    () => context.settings?.current?.[PAUSE_ANIMATIONS_FEATURE_KEY]?.value !== 0,
    [context.settings.current],
  );

  const footer = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(
    () => {
      if ((!context.isOpen && shown) || !footer.current || isPauseAnimationFeatureActive) {
        return;
      }

      // After animation, it resets height and overflow, also sets `shown` flag
      footer.current.addEventListener(
        'transitionend',
        () => {
          footer.current.style.height = 'auto';
          footer.current.style.overflow = '';

          setShown(true);
        },
        { once: true },
      );

      // Changes height to show footer
      // note: delay done in JS not CSS so `scrollHeight` does not equal to 0
      setTimeout(() => {
        footer.current.style.height = `${footer.current.scrollHeight}px`;
      }, FOOTER_ANIMATION_DELAY);
    },
    // Runs effect callback after widget is open
    [context.isOpen, isPauseAnimationFeatureActive],
  );

  const isHiddenNavMenuButtons =
    !(isFooterNavLinksVisible && accessibilityStatementVisible) &&
    !(isFooterNavLinksVisible && manageLinkVisible) &&
    !(isFooterNavLinksVisible && reportProblemVisible) &&
    !(isLogged && backState) &&
    isCustomLogoEnvironment;

  let footerClasses = '';
  if (isPauseAnimationFeatureActive) {
    footerClasses += ' uwaw-footer-paused-animation';
  }
  if (asteriskVisible) {
    footerClasses += ' uwaw-footer_has-asterisk';
  }
  if (isCustomLogoEnvironment) {
    footerClasses += ' uwaw-footer_has-brand';
  }
  if (isFooterNavLinksVisible && accessibilityStatementVisible) {
    footerClasses += ' uwaw-footer_has-has-statement';
  }

  return (
    context && (
      <div ref={footer} className={`uwaw-footer ${footerClasses}`}>
        {!isHiddenNavMenuButtons && (
          <div className="uwaw-footer__i">
            <div className="uwaw-footer__l">
              <ul className="uwaw-footer__nav">
                {isFooterNavLinksVisible && accessibilityStatementVisible && (
                  <li className="uwaw-footer__nav__item">
                    <a
                      className="uwaw-footer__nav__lnk"
                      href={accessibilityStatementLink}
                      target="_blank"
                      aria-label={`${accessibilityStatementText} - ${translate('widget.new_tab')}`}
                    >
                      <span className="uwaw-features__nav_truncate">
                        {truncate(accessibilityStatementText, 50)}
                      </span>
                    </a>
                  </li>
                )}
                {isFooterNavLinksVisible && reportProblemVisible && (
                  <li className="uwaw-footer__nav__item">
                    <button
                      className="uwaw-footer__nav__lnk"
                      tabIndex={0}
                      onClick={createGoTo('report')}
                      onKeyUp={onKeyboardSelection(createGoTo('report'))}
                      disabled={disabledGoToBtn}
                    >
                      {translate('widget.footer.report.label')}
                    </button>
                  </li>
                )}
                {isFooterNavLinksVisible && manageLinkVisible && (
                  <li className="uwaw-footer__nav__item">
                    <button
                      className="uwaw-footer__nav__lnk uwaw-footer__nav__lnk_mng"
                      onClick={createGoTo('login')}
                      onKeyUp={onKeyboardSelection(createGoTo('login'))}
                      disabled={disabledGoToBtn}
                      tabIndex={0}
                      aria-label={translate(
                        isWhiteLabeled
                          ? 'widget.footer.manage_whitelabel.aria'
                          : 'widget.footer.manage.aria',
                      )}
                    >
                      {translate('widget.footer.manage.label')}
                    </button>
                  </li>
                )}

                {isLogged &&
                  backState &&
                  context.widgetLayout !== 'headless' &&
                  !context.tuningsFromServerInProgress && (
                    <li className="uwaw-footer__nav__item">
                      <button
                        className="uwaw-footer__nav__lnk uwaw-footer__nav__lnk_mng"
                        onClick={createGoTo(isMainMenuUrl ? 'mainMenu' : 'backState')}
                        onKeyUp={onKeyboardSelection(
                          createGoTo(isMainMenuUrl ? 'mainMenu' : 'backState'),
                        )}
                        disabled={disabledGoToBtn}
                        tabIndex={0}
                      >
                        {translate('widget.footer.misc.main_menu')}
                      </button>
                    </li>
                  )}
              </ul>
            </div>
            {!isCustomLogoEnvironment && <div className="uwaw-footer__r">{<Logo />}</div>}
          </div>
        )}

        <AdaguardianLogo />

        <ShortpointLogo />

        <PortcitymarketingLogo />

        <LeosLogo />

        <DigitouchLogo />

        <NagishLiLogo />

        <NazoozLogo />

        <AltmeyerLogo />

        <JoyDigitalLogo />

        <MadeByCatLogo />

        <XperientsLogo />

        <SorceLogo />

        <OctecLogo />

        {asteriskVisible && (
          <button
            className="uwaw-footer__asterisk"
            onClick={createGoTo('login')}
            onKeyUp={onKeyboardSelection(createGoTo('login'))}
            disabled={disabledGoToBtn}
            tabIndex={0}
            aria-label={translate(
              isWhiteLabeled ? 'widget.footer.manage_whitelabel.aria' : 'widget.footer.manage.aria',
            )}
          >
            *
          </button>
        )}

        <GenericPartnerLogo />
      </div>
    )
  );
};
